import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { TestCardContext } from "@app/provider";
import { ParsedTestCard } from "@app/provider/testData/types";
import {
  isGisEligibleStatus,
  isGisEligibleTestType,
  isWithinGisRecencyWindow,
} from "@app/utils/gisEntrySource";

/**
 * Query-param key used by the multi-order picker (`PublicGisOrderSelect`) to
 * tell the triage page which eligible order the patient chose. Triage uses it
 * to bypass the single-order auto-select rule and route the chosen order.
 */
export const GIS_SELECTED_ORDER_PARAM = "orderUid";

/**
 * Returns the patient's GIS-eligible orders sorted newest-first, with at most
 * one order per test type.
 *
 * Filters out cancelled / expired tests via `isGisEligibleStatus` and PROSPERA
 * via `isGisEligibleTestType` so they are excluded from the eligible-order
 * count used for downstream routing (no-orders vs single-order vs multi-order
 * selection). PROSPERA has no GIS, so a patient whose only order is PROSPERA
 * falls through to the no-order pre-test intake flow rather than the "not
 * available" state.
 *
 * Orders created more than three months ago are also excluded via the shared
 * `isWithinGisRecencyWindow` rule so stale orders are neither auto-selected on
 * triage nor shown in the multi-order picker.
 *
 * RENASIGHT is intentionally *kept* eligible (it has a Renasight-specific GIS
 * scheduling page), so it appears in the multi-order picker alongside other
 * tests; triage routes a selected Renasight order to that external page.
 *
 * When an account holds multiple tests of the same test type, only the latest
 * (newest by `createdAt`) is kept. GIS counseling is product-specific, not
 * order-specific, so duplicate test types add no value to the multi-order
 * picker. Collapsing them means a patient whose only distinct product is, say,
 * several Signatera draws is treated as having a single eligible order and is
 * routed straight through the "UPP auth → single order → direct scheduling"
 * flow instead of being shown a picker full of effectively-identical choices.
 */
export function getEligibleOrders(
  cards: ParsedTestCard[] | undefined
): ParsedTestCard[] {
  if (!cards?.length) return [];
  const sorted = [...cards]
    .filter((c) => isGisEligibleStatus(c.status))
    .filter((c) => isGisEligibleTestType(c.testType))
    .filter((c) => isWithinGisRecencyWindow(c.createdAt))
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

  // Keep only the first (newest) order per test type. `sorted` is already
  // newest-first, so the first occurrence of each test type is the latest one.
  const seenTestTypes = new Set<ParsedTestCard["testType"]>();
  return sorted.filter((card) => {
    if (seenTestTypes.has(card.testType)) return false;
    seenTestTypes.add(card.testType);
    return true;
  });
}

export interface EligibleGisOrdersState {
  eligibleOrders: ParsedTestCard[];
  isLoading: boolean;
  loadError: boolean;
  retry: () => void;
}

/**
 * Fetches the patient's primary test cards once on mount and exposes the
 * GIS-eligible subset along with loading / error / retry state.
 *
 * Shared by `PublicGisTriage` (single-order auto-routing) and
 * `PublicGisOrderSelect` (multi-order picker) so both surfaces apply the
 * exact same eligibility rules and fetch semantics.
 */
export function useEligibleGisOrders(): EligibleGisOrdersState {
  const { getPrimaryTestCards, primaryTestCardsLoading } =
    useContext(TestCardContext);

  const [cards, setCards] = useState<ParsedTestCard[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  // Increments on every fetch attempt so stale resolutions from a previous
  // attempt (e.g. before the user hit "Retry") can't overwrite newer state.
  const fetchTokenRef = useRef(0);

  const fetchCards = useCallback(async () => {
    const token = ++fetchTokenRef.current;
    setCards(null);
    setLoadError(false);
    try {
      // Use the same recency window as the homepage (the default
      // `CompletedTestsFilter.ACTUAL` path) so completed results older than the
      // homepage's window are excluded here too. A patient whose only orders are
      // old completed results sees "no tests" on the homepage, and likewise has
      // no GIS-eligible orders — they fall through to the no-order intake flow
      // rather than the public funnel resurfacing stale completed orders.
      const result = await getPrimaryTestCards({ includeCompleted: true });
      if (token !== fetchTokenRef.current) return;
      if (!Array.isArray(result)) {
        setLoadError(true);
        return;
      }
      setCards(result);
    } catch (err) {
      console.error("[GIS] failed to load primary test cards", err);
      if (token !== fetchTokenRef.current) return;
      setLoadError(true);
    }
  }, [getPrimaryTestCards]);

  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    void fetchCards();
  }, [fetchCards]);

  const eligibleOrders = useMemo(
    () => getEligibleOrders(cards ?? undefined),
    [cards]
  );

  const isLoading = primaryTestCardsLoading || (cards === null && !loadError);

  return {
    eligibleOrders,
    isLoading,
    loadError,
    retry: () => void fetchCards(),
  };
}
